import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useWorkers, useDeactivateWorker, useReactivateWorker } from '@/hooks/useWorkers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export default function WorkerList() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [deactivateWorkerId, setDeactivateWorkerId] = useState<string | null>(null);
  const [reactivateWorkerId, setReactivateWorkerId] = useState<string | null>(null);

  const { data: allWorkers, isLoading, error } = useWorkers(showInactive);
  const { mutateAsync: deactivateWorker } = useDeactivateWorker();
  const { mutateAsync: reactivateWorker } = useReactivateWorker(reactivateWorkerId || '');

  // Client-side filtering
  const workers = useMemo(() => {
    if (!allWorkers) return [];
    if (!searchInput.trim()) return allWorkers;

    const searchLower = searchInput.toLowerCase();
    return allWorkers.filter((worker) => {
      const nameMatch = worker.name?.toLowerCase().includes(searchLower);
      const calendarIdMatch = worker.calendarId?.toLowerCase().includes(searchLower);
      return nameMatch || calendarIdMatch;
    });
  }, [allWorkers, searchInput]);

  const handleDeactivate = async () => {
    if (!deactivateWorkerId) return;

    try {
      await deactivateWorker(deactivateWorkerId);
      setDeactivateWorkerId(null);
    } catch (error) {
      console.error('Failed to deactivate worker:', error);
    }
  };

  const handleReactivate = async () => {
    if (!reactivateWorkerId) return;

    try {
      await reactivateWorker();
      setReactivateWorkerId(null);
    } catch (error) {
      console.error('Failed to reactivate worker:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-600">Error loading workers: {error.message}</div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Configure worker Google Calendar IDs for timesheet tracking
          </p>
          <Link to="/settings/workers/new">
            <Button>Add Worker</Button>
          </Link>
        </div>

        {/* Search and Filters */}
        <div className="mt-4 flex gap-4">
          <Input
            type="search"
            placeholder="Search workers by name or calendar ID..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="max-w-md"
          />
          <Button
            variant={showInactive ? 'default' : 'outline'}
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? 'Hide Inactive' : 'Show Inactive'}
          </Button>
        </div>
      </div>

      {/* Workers Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Name</TableHead>
              <TableHead>Calendar ID</TableHead>
              <TableHead className="w-[120px] text-right">Rate</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead className="w-[180px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {workers && workers.length > 0 ? (
              workers.map((worker) => (
                <TableRow
                  key={worker.id}
                  className={!worker.active ? 'bg-gray-50 opacity-60' : ''}
                >
                  <TableCell className="font-medium">{worker.name}</TableCell>
                  <TableCell className="font-mono text-sm max-w-[200px] truncate" title={worker.calendarId}>{worker.calendarId}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {worker.rate === null || worker.rate === undefined ? (
                      <span className="text-gray-400">&mdash;</span>
                    ) : (
                      `$${worker.rate.toFixed(2)}`
                    )}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        worker.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {worker.active ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/settings/workers/${worker.id}/edit`)}
                      >
                        Edit
                      </Button>
                      {worker.active ? (
                        <Dialog
                          open={deactivateWorkerId === worker.id}
                          onOpenChange={(open: boolean) =>
                            setDeactivateWorkerId(open ? worker.id : null)
                          }
                        >
                          <DialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                              Deactivate
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Deactivate Worker</DialogTitle>
                              <DialogDescription>
                                Are you sure you want to deactivate {worker.name}? They will
                                be excluded from timesheet calculations. You can reactivate
                                them later if needed.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <Button
                                variant="outline"
                                onClick={() => setDeactivateWorkerId(null)}
                              >
                                Cancel
                              </Button>
                              <Button variant="destructive" onClick={handleDeactivate}>
                                Deactivate
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      ) : (
                        <Dialog
                          open={reactivateWorkerId === worker.id}
                          onOpenChange={(open: boolean) =>
                            setReactivateWorkerId(open ? worker.id : null)
                          }
                        >
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              Reactivate
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Reactivate Worker</DialogTitle>
                              <DialogDescription>
                                Are you sure you want to reactivate {worker.name}? They will
                                be included in timesheet calculations again.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <Button
                                variant="outline"
                                onClick={() => setReactivateWorkerId(null)}
                              >
                                Cancel
                              </Button>
                              <Button onClick={handleReactivate}>Reactivate</Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <div className="text-gray-500">
                    <p className="text-lg font-medium">No workers found</p>
                    <p className="text-sm mt-1">
                      {searchInput
                        ? 'Try adjusting your search terms'
                        : 'Get started by adding your first worker'}
                    </p>
                    {!searchInput && (
                      <Link to="/settings/workers/new">
                        <Button className="mt-4">Add Worker</Button>
                      </Link>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
